'use client';

import React from 'react';
import {
  PiCalendarBlank,
  PiLightningFill,
} from 'react-icons/pi';
import { CiLocationOn } from 'react-icons/ci';
import EventBannerImage from '../atoms/EventBannerImage/EventBannerImage';
import usePrefetchOnIntent from '../../hooks/usePrefetchOnIntent';
import type { FeaturedSlide } from '../../utils/buildFeaturedSlides';

export type { FeaturedSlide };

interface Props {
  slide: FeaturedSlide;
  onClick?: (href: string) => void;
  priority?: boolean;
}

export const Slide: React.FC<Props> = ({ slide, onClick, priority = false }) => {
  const { href } = slide;
  const prefetchHandlers = usePrefetchOnIntent(href);
  return (
    <a
      href={href}
      onClick={(e) => {
        // Clicking a peek slide should bring it to the center (handled by
        // slick's `focusOnSelect`); only the center slide opens the event.
        // Defaults to true for the no-carousel case where the `.slick-slide`
        // wrapper doesn't exist — which includes our SSR static placeholder.
        const slideEl = (e.currentTarget as HTMLElement).closest('.slick-slide');
        const isCenter = slideEl?.classList.contains('slick-center') ?? true;
        if (!isCenter) {
          e.preventDefault();
          return;
        }
        if (onClick) {
          e.preventDefault();
          onClick(href);
        }
      }}
      {...prefetchHandlers}
      className="relative block w-full aspect-[4/5] sm:aspect-[16/9] md:aspect-[1920/700] overflow-hidden rounded-3xl leading-[0] bg-surface-secondary"
    >
      <EventBannerImage
        src={slide.bannerImage || '/favicon.png'}
        alt={slide.title}
        priority={priority}
        sizes="100vw"
        className="object-cover object-center"
      />
      <div className="md:hidden absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.45)_0%,rgba(0,0,0,0)_22%,rgba(0,0,0,0)_45%,rgba(0,0,0,0.92)_100%)]" />
      <div className="hidden md:block absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

      <span className="absolute top-4 right-4 md:top-6 md:right-10 inline-flex items-center gap-1 md:gap-1.5 bg-primary-500 text-white uppercase font-bold tracking-wider text-[11px] md:text-xs px-3 md:px-3.5 py-1.5 md:py-1.5 rounded-full shadow-md z-[2]">
        <PiLightningFill className="text-[11px] md:text-sm" />
        Destacado
      </span>

      <div className="absolute bottom-0 left-0 right-0 px-5 pb-5 md:px-10 lg:px-14 md:pb-8 lg:pb-10 flex flex-col gap-2 md:gap-3 leading-normal">
        <h2 className="text-white font-bold leading-tight text-2xl sm:text-3xl md:text-3xl lg:text-5xl max-w-2xl m-0 [text-shadow:0_2px_18px_rgba(0,0,0,0.65)] md:drop-shadow-lg md:[text-shadow:none]">
          {slide.title}
        </h2>
        {slide.subtitle && (
          <p className="text-white/95 font-semibold text-sm md:text-lg max-w-2xl m-0 [text-shadow:0_1px_10px_rgba(0,0,0,0.6)] md:[text-shadow:none]">
            {slide.subtitle}
          </p>
        )}
        <div className="flex flex-wrap gap-x-3 gap-y-1 md:gap-x-7 md:gap-y-2 text-white/95 text-sm md:text-base font-medium [text-shadow:0_1px_10px_rgba(0,0,0,0.6)] md:[text-shadow:none]">
          {slide.dateLabel && (
            <span className="inline-flex items-center gap-1.5 md:gap-2">
              <PiCalendarBlank className="text-base md:text-lg" />
              {slide.dateLabel}
            </span>
          )}
          {slide.place && (
            <span className="inline-flex items-center gap-1 md:gap-2">
              <CiLocationOn className="text-lg md:text-xl" />
              {slide.place}
            </span>
          )}
        </div>
        <span className="md:hidden mt-1 inline-flex w-fit items-center bg-white text-primary-500 font-bold text-sm px-5 py-2.5 rounded-full shadow-lg">
          {slide.ctaLabel}
        </span>
      </div>
    </a>
  );
};
